import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Receipt } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { AgriCampaign, AgriCampaignCharge } from '../../utils/agriEngine';
import { formatEur } from '../../utils/agriEngine';
import { useAgriCampaignCharges } from '../../hooks/useAgriculture';

interface Props {
  campaign: AgriCampaign;
  onClose: () => void;
}

const CHARGE_PRESETS = ['Semences', 'Engrais', 'Phytosanitaires', 'Fuel & lubrifiants', 'Main d\'œuvre', 'Location matériel', 'Assurances', 'Autres charges'];

export function ChargesModal({ campaign, onClose }: Props) {
  const { charges, refresh } = useAgriCampaignCharges(campaign.id);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: '', amount: '', charge_date: '', charge_type: 'variable' as const, notes: '' });
  const [saving, setSaving] = useState(false);

  const totalCharges = charges.reduce((s, c) => s + c.amount, 0);

  async function handleAdd() {
    if (!form.label || !form.amount) return;
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }
    await supabase.from('agri_campaign_charges').insert({
      user_id: user.id,
      campaign_id: campaign.id,
      label: form.label,
      amount: parseFloat(form.amount),
      charge_date: form.charge_date || null,
      charge_type: form.charge_type,
      notes: form.notes || null,
    });
    setForm({ label: '', amount: '', charge_date: '', charge_type: 'variable', notes: '' });
    setShowForm(false);
    setSaving(false);
    refresh();
  }

  async function handleDelete(id: string) {
    await supabase.from('agri_campaign_charges').delete().eq('id', id);
    refresh();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Charges — {campaign.name}</h2>
            <p className="text-sm text-gray-500 mt-0.5">Total : <span className="font-semibold text-red-600">{formatEur(totalCharges)}</span></p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {charges.length === 0 && !showForm && (
            <div className="text-center py-8 text-gray-400">
              <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Aucune charge enregistrée</p>
            </div>
          )}

          {charges.length > 0 && (
            <table className="w-full text-sm mb-4">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 text-gray-500 font-medium">Poste</th>
                  <th className="text-left py-2 text-gray-500 font-medium">Type</th>
                  <th className="text-left py-2 text-gray-500 font-medium">Date</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Montant</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {charges.map(c => (
                  <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2.5 font-medium text-gray-800">{c.label}</td>
                    <td className="py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.charge_type === 'fixed' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                        {c.charge_type === 'fixed' ? 'Fixe' : 'Variable'}
                      </span>
                    </td>
                    <td className="py-2.5 text-gray-500">{c.charge_date ? new Date(c.charge_date).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : '—'}</td>
                    <td className="py-2.5 text-right font-semibold text-red-600">{formatEur(c.amount)}</td>
                    <td className="py-2.5 text-right">
                      <button onClick={() => handleDelete(c.id)} className="p-1 text-gray-400 hover:text-red-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {showForm && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-200">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Poste de charge *</label>
                  <input list="charge-presets" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                    placeholder="Semences, Engrais..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
                  <datalist id="charge-presets">{CHARGE_PRESETS.map(p => <option key={p} value={p} />)}</datalist>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Montant (€) *</label>
                  <input type="number" min="0" step="100" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    placeholder="4500" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date prévue</label>
                  <input type="date" value={form.charge_date} onChange={e => setForm(f => ({ ...f, charge_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                  <select value={form.charge_type} onChange={e => setForm(f => ({ ...f, charge_type: e.target.value as 'variable' | 'fixed' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm">
                    <option value="variable">Variable</option>
                    <option value="fixed">Fixe</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowForm(false)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-gray-600 text-sm hover:bg-gray-50">Annuler</button>
                <button onClick={handleAdd} disabled={saving} className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
                  {saving ? 'Ajout...' : 'Ajouter'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100 flex-shrink-0">
          {!showForm && (
            <button onClick={() => setShowForm(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 text-sm font-medium transition-colors">
              <Plus className="w-4 h-4" />
              Ajouter une charge
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
